<template>
  <ui-menu-base-trigger>
    <template #default="{ openMenu }">
      <button
        data-testid="dropdown-tags-trigger"
        :disabled="disabled"
        type="button"
        class="border-gray-30 flex min-h-8 w-full items-center rounded border bg-white px-2 py-1.5"
        :aria-expanded="expanded"
        @focus="focusInput"
      >
        <div class="flex grow flex-wrap gap-2 overflow-auto">
          <ui-tag
            v-for="option in selectedOptions"
            :key="String(option[props.propToCheck])"
            :capitalized="false"
            dismissible
            @dismiss="$emit('toggle', option)"
          >
            {{ option[props.label] }}
          </ui-tag>
          <input
            ref="input"
            class="grow text-xs font-normal outline-none placeholder:text-gray-50"
            :value="searchQuery"
            :placeholder="isEmpty ? triggerPlaceholder : ''"
            @input="handleSearchChange"
            @keydown="handleKeydown"
            @focus="openMenu"
          />
        </div>
        <double-caret-icon class="icon-m text-gray-60 ml-auto flex shrink-0 duration-200 ease-out" />
      </button>
    </template>
  </ui-menu-base-trigger>
</template>

<script
  setup
  lang="ts"
  generic="T extends object, P extends keyof T, L extends keyof T, M extends Modes, V extends T, O extends boolean"
>
import { computed, ref, inject } from 'vue'
import UiTag from '../UiTag.vue'
import { type MenuState } from '../UiMenu/types'
import UiMenuBaseTrigger from '../UiMenu/triggers/UiMenuBaseTrigger.vue'

import DoubleCaretIcon from './assets/double-caret.svg'
import { type DropdownProps, type Modes } from './types'
import type useList from './composables/useList'

interface Props extends DropdownProps<T, P, L, M, V, O> {
  expanded: boolean
  selectedOptions: ReturnType<typeof useList<T, P, L, M, V, O>>['selectedOptions']['value']
  label: L
  searchQuery: string
  dropdownVisible: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  toggle: [option: T]
  'search-change': [query: string]
  'add-option': [query: string]
}>()

const menuState = inject<MenuState>('menuState')

const input = ref<HTMLInputElement | null>(null)
const isEmpty = computed(() => !props.selectedOptions.length)

const handleSearchChange = (event: Event) => {
  emit('search-change', (event.target as HTMLInputElement)?.value)
}

const addingDisabled = computed(() => !!menuState?.activeItemId.value)

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
  } else if (event.key === 'Enter' && !addingDisabled.value && props.searchQuery.trim() !== '') {
    emit('add-option', props.searchQuery)
  } else if (event.key === 'Backspace' && !props.searchQuery && props.selectedOptions.length) {
    emit('toggle', props.selectedOptions[props.selectedOptions.length - 1])
  }
}

const focusInput = () => input.value?.focus()

defineExpose({ focus: focusInput })
</script>
